import React, { Component } from "react";
import { Route, Redirect, Switch, withRouter } from "react-router-dom";
import { getCurrentUser, fetchAuthSession } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { BackDrop } from "./components/BackDrop/BackDrop";
import { ProposalStepGuide } from "./components/ProposalStepGuide/proposalStepGuide";
import {
  getSubmissions,
  getAdditionalSubmissions,
  getProposalTypes,
  getCustomInfo,
  getRegistrationTypes,
  getRegistrationCategoryTypes,
  getCustodians,
  getLogos,
} from "./services/submissionService";
import { getVersions } from "./services/versionService";
import auth from "./services/authService";
import NavBar from "./components/navBar";
import LoginForm from "./components/loginForm";
import NotFound from "./components/notFound";
import Logout from "./components/logout";
import SideDrawer from "./components/SideDrawer/SideDrawer";
import ProtectedRoute from "./components/common/protectedRoute";
import ResetPassword from "./components/forgotPassword";
import User from "./components/user";
import LeftAlertDrawer from "./components/LeftAlerts/LeftAlertDrawer";
import Proposals from "./components/Proposals/proposals";
import ProposalSubmissions from "./components/ProposalSubmissions/proposalSubmissions";
import UpdatePropState from "./context/updatePropState";
import UpdateFormProposalType from "./context/updateFormProposalType";
import SessionLockModal from "./components/SessionLock/SessionLockModal";
import {
  clearSessionLockCookies,
  enterParticipatingContext,
  getActivityThrottleMs,
  getWarningState,
  isLocallyLocked,
  isSessionLockEnabled,
  lockSession,
  markActivity,
  markPresence,
  reconcileParticipatingContext,
  subscribeToLockEvents,
  verifyUnlock,
} from "./services/sessionLockService";
import {
  isEdgeSessionEnabled,
  sendHeartbeat,
} from "./services/sessionEdgeService";

import "./App.css";
import {
  formatIspAccountGoalForDisplay,
  isIspOnlyStrategy,
  isIspOnlyProgram,
} from "./utils/proposalUtils";
import ProposalDocuments from "./components/Documents/proposalDocuments";
import FusionProposal from "./components/FusionProposal/fusionProposal";
import { ISPStepGuide } from "./components/ISP/ispStepGuide";
import SupportPage from "./components/Support/SupportPage";
import { getSupportProfile } from "./components/Support/supportService";
import { normalizeIspEditDateTime } from "./utils/ispEdit";

// Session security constants
const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes (FINRA guideline)
const INACTIVITY_WARNING_MS = 13 * 60 * 1000; // 13 minutes (warning appears)
const WARNING_DURATION_MS = 2 * 60 * 1000; // 2-minute warning window
const TOKEN_REFRESH_THRESHOLD_MS = 2 * 60 * 1000; // refresh tokens 2 min before expiry

export class App extends Component {
  _isMounted = true;
  _edgeSessionEnabled = isEdgeSessionEnabled();

  // Inactivity tracking
  lastActivityTime = Date.now();
  _inactivityWarningVisible = false;
  _warningStartTime = null;
  _isRefreshing = false;
  _lastActivityUpdate = 0;
  state = {
    loggedIn: false,
    sideDrawerOpen: false,
    leftAlertDrawerOpen: false,
    user: "",
    alertMessage: "",
    alertButtonMessage: "",
    confirmFunction: null,
    advisors: [],
    sessionExpireNotificaiton: false,
    authSession: null, // Store Amplify v6 session object
    refreshRequested: false,
    proposalTypes: [],
    proposals: [],
    additionalSubmissions: [], // Additional submissions data for pagination
    registrationTypes: [],
    registrationCategoryTypes: [],
    custodians: [],
    logos: [],
    versions: {},
    formProposalType: "",
    updateTime: null,
    editProposal: null,
    formRequestType: null,
    userInfo: [],
    details: false,
    submissionsRequestSent: false,
    userStatusRequestSent: false,
    submissionsLastKey: "",
    insertType: "",
    checkForUpdates: false,
    fusionStepGuideData: null, // Store data from step-guide for fusion workflow
    fusionListMode: false, // Flag to indicate when we're in fusion list mode
    warningRemainingSeconds: 0,
    lastUpdateDate: "",
    // Loading states for better UX
    proposalTypesLoading: false,
    userDataLoading: false, // Loading state for user data (enables card buttons)
    submissionsLoading: false, // Separate loading state for submissions data
    logosLoading: false,
    stepGuideDataLoading: false, // Separate loading state for step-guide required data
    dataLoaded: false, // Flag to prevent repeated data loading
    ispReferenceClients: [], // ISP clients to show as reference banner in proposal form
    ispReferenceAmount: "",
    ispReferenceGoal: "",
    ispReferenceFusionAllocations: [],
    sessionLockEnabled: isSessionLockEnabled(),
    sessionLockWarningVisible: false,
    sessionLockRemainingSeconds: 0,
    sessionLocked: false,
    sessionLockCode: "",
    sessionLockError: "",
    sessionLockSubmitting: false,
    supportAccess: false,
  };

  handleLoggedIn = () => {
    this.setState({ loggedIn: true });
  };

  handleLogOut = () => {
    this.setState({ loggedIn: false, supportAccess: false });
  };

  loadSupportAccess = async () => {
    try {
      const profile = await getSupportProfile();
      if (this._isMounted) {
        this.setState({ supportAccess: profile?.has_access === true });
      }
    } catch (error) {
      console.warn("Support access lookup failed:", error);
      if (this._isMounted) this.setState({ supportAccess: false });
    }
  };

  getISPReferenceState = (ispData = {}) => ({
    ispReferenceClients: Array.isArray(ispData.ispClients)
      ? ispData.ispClients
      : [],
    ispReferenceAmount: ispData.ispAmount || "",
    ispReferenceGoal: ispData.ispGoal || "",
    ispReferenceFusionAllocations: Array.isArray(ispData.ispFusionAllocations)
      ? ispData.ispFusionAllocations
      : [],
  });

  // Check if ISP stored proposal data for pre-filling the proposal form
  checkForISPProposalData = () => {
    try {
      const stored = sessionStorage.getItem("ispProposalData");
      if (stored) {
        sessionStorage.removeItem("ispProposalData");
        const ispData = JSON.parse(stored);
        this.setState({
          editProposal: ispData,
          formRequestType: "Copy",
          formProposalType: ispData.program || "",
          ...this.getISPReferenceState(ispData),
        });
        return true;
      }
    } catch (e) {
      console.error("Error reading ISP proposal data:", e);
      sessionStorage.removeItem("ispProposalData");
    }
    return false;
  };

  dismissISPReferenceBanner = () => {
    this.setState({
      ispReferenceClients: [],
      ispReferenceAmount: "",
      ispReferenceGoal: "",
      ispReferenceFusionAllocations: [],
    });
  };

  clearProposalState = () => {
    this.setState({
      editProposal: null,
      formRequestType: null,
      ...this.getISPReferenceState(),
    });
  };

  // Navigate from ISP to proposal step guide with pre-filled data (client-side, no reload)
  navigateToProposalFromISP = (ispData) => {
    this.setState(
      {
        editProposal: ispData,
        formRequestType: ispData.DateTime ? "Edit" : "Copy",
        formProposalType: ispData.program || "",
        ...this.getISPReferenceState(ispData),
      },
      () => {
        this.props.history.push("/proposals/step-guide");
      },
    );
  };

  // Create proposal from an ISP submission record (submissions table)
  createProposalFromISPSubmission = (ispRecord) => {
    // Block proposal creation when the submission's strategy is ISP-only.
    if (isIspOnlyProgram(ispRecord.program, this.state.proposalTypes)) {
      alert(
        `"${ispRecord.program}" is an ISP-only strategy and cannot be used to create a proposal.`,
      );
      return;
    }
    // Parse joint clients if stored as JSON string
    let jointClients = [];
    let ispFormData = {};
    try {
      jointClients = ispRecord.ispJointClients
        ? JSON.parse(ispRecord.ispJointClients)
        : [];
    } catch (e) {
      jointClients = [];
    }
    try {
      ispFormData = ispRecord.ispFormData
        ? JSON.parse(ispRecord.ispFormData)
        : {};
    } catch (e) {
      ispFormData = {};
    }
    const referenceClients =
      jointClients.length > 0
        ? jointClients
        : Array.isArray(ispFormData.jointClients)
          ? ispFormData.jointClients
          : [];
    const referenceGoal =
      ispRecord.ispPrimaryGoal ||
      formatIspAccountGoalForDisplay(
        ispFormData.accountGoal,
        ispFormData.accountGoalOther,
      );
    const referenceAmount =
      ispRecord.VALUE || ispFormData.initialInvestmentAmount || "";
    const fusionAccountNumbers = Array.isArray(ispFormData.accountNumbers)
      ? ispFormData.accountNumbers.filter((accountNumber) =>
          accountNumber?.trim?.(),
        )
      : ispFormData.accountNumber
        ? [String(ispFormData.accountNumber).trim()].filter(Boolean)
        : [];
    const ispRiskCategory = ispFormData.riskScores?.riskCategory || "";
    const ispSelectedPortfolio =
      ispFormData.riskScores?.selectedPortfolio ||
      ispRecord["RISK TOLERANCE"] ||
      "";

    const proposalData = {
      DateTime: ispRecord.DateTime || "",
      "CLIENT NAMES": ispRecord["CLIENT NAMES"] || "",
      "ADVISOR NAME": ispRecord["ADVISOR NAME"] || "",
      customTemplate: ispRecord["ADVISOR NAME"] || "",
      program: ispRecord.program || "",
      REGISTRATION: ispRecord.REGISTRATION || "",
      registrations: ispRecord.REGISTRATION
        ? [
            {
              registration: "registration0",
              value: ispRecord.REGISTRATION,
              percentage: "100",
            },
          ]
        : undefined,
      "RISK TOLERANCE": ispRecord["RISK TOLERANCE"] || "",
      VALUE: ispRecord.VALUE || "",
      assetValue: ispRecord.VALUE || "",
      BOND: ispRecord.ispBondType || "",
      optionsTM: ispRecord.ispBondType || "",
      incomeNeeds: "",
      logoSize: "",
      logoOffset: "",
      papercheck: "true",
      othercheck: "",
      insertType: "",
      fusionAccountNumbers: fusionAccountNumbers,
      isNewFusionClient: ispFormData.requestType
        ? ispFormData.requestType === "New"
        : fusionAccountNumbers.length === 0,
      ispClients: referenceClients.map((c) => ({
        name: c.clientName || c.name || "",
        isPrimary: c.isPrimary,
      })),
      ispAmount: referenceAmount,
      ispGoal: referenceGoal,
      ispRiskCategory: ispRiskCategory,
      ispSelectedPortfolio: ispSelectedPortfolio,
      ispFusionAllocations: Array.isArray(ispFormData.fusionAllocations)
        ? ispFormData.fusionAllocations
        : [],
    };

    this.navigateToProposalFromISP(proposalData);
  };

  // Create ISP from a proposal submission record (submissions table)
  createISPFromProposalSubmission = (proposalRecord) => {
    let proposalFormData = {};
    try {
      proposalFormData = proposalRecord.proposalFormData
        ? JSON.parse(proposalRecord.proposalFormData)
        : {};
    } catch (e) {
      proposalFormData = {};
    }

    // Store proposal data for ISP pre-fill
    const ispPrefill = {
      advisorName: proposalRecord["ADVISOR NAME"] || "",
      strategy: proposalRecord.program || "",
      registration:
        proposalFormData.registrations || proposalRecord.REGISTRATION || "",
      clientName: proposalRecord["CLIENT NAMES"] || "",
      riskTolerance:
        proposalRecord["RISK TOLERANCE"] || proposalRecord.RISK || "",
      bondType: proposalRecord.BOND || "",
      accountValue: proposalRecord.VALUE || "",
      isFusion: (proposalRecord.program || "").toLowerCase().includes("fusion"),
      dateTime: proposalRecord.DateTime || "",
    };

    sessionStorage.setItem("proposalToISPData", JSON.stringify(ispPrefill));
    this.props.history.push("/plan/step-guide");
  };

  // Edit an existing ISP submission — stores form data in sessionStorage and navigates to ISP form
  editISPSubmission = (proposal) => {
    const ispFormData = proposal.ispFormData;
    if (!ispFormData) {
      alert("Edit data is not available for this ISP submission.");
      return;
    }
    const editDateTime = normalizeIspEditDateTime(proposal.DateTime);
    if (!editDateTime) {
      alert("The original ISP submission date is not available for editing.");
      return;
    }
    sessionStorage.setItem("editISPData", ispFormData);
    sessionStorage.setItem("editISPDateTime", editDateTime);
    this.props.history.push("/plan/step-guide");
  };

  // Edit an existing Proposal submission — maps proposalFormData back to editProposal shape
  editProposalSubmission = (proposal) => {
    const rawData = proposal.proposalFormData;
    if (!rawData) {
      alert("Edit data is not available for this proposal submission.");
      return;
    }
    try {
      const data = JSON.parse(rawData);

      // Extract values from form-info array
      const formInfo = data["form-info"] || [];
      const getFormValue = (key) => {
        const entry = formInfo.find((item) => item.key === key);
        return entry ? entry.value : "";
      };

      const editProposal = {
        DateTime: proposal.DateTime || "",
        program: data.program || "",
        "CLIENT NAMES": getFormValue("CLIENT NAMES"),
        "ADVISOR NAME": getFormValue("ADVISOR NAME"),
        customTemplate: data.customTemplate || getFormValue("ADVISOR NAME"),
        LOGO: getFormValue("LOGO"),
        REGISTRATION: getFormValue("REGISTRATION"),
        "RISK TOLERANCE":
          getFormValue("RISK TOLERANCE") || getFormValue("RISK"),
        "insert-pdf": getFormValue("insert-pdf"),
        VALUE: getFormValue("VALUE"),
        assetValue: data.assetValue || getFormValue("VALUE"),
        incomeNeeds: data.incomeNeeds || getFormValue("INCOME"),
        optionsTM: data.optionsTM || "",
        papercheck: data.papercheck,
        othercheck: data.othercheck,
        logoSize: data.logoSize || "",
        logoOffset: data.logoOffset || "",
        registrations: data.registrations || undefined,
        insertType: data.insertType || "",
        fusionPercentage: data.fusionPercentage || 0,
        fusionAmount: data.fusionAmount || 0,
        isNewFusionClient:
          data.isNewFusionClient !== undefined ? data.isNewFusionClient : true,
        fusionAccountNumbers: data.fusionAccountNumbers || [],
      };

      this.setState({ editProposal, formRequestType: "Edit" }, () => {
        this.props.history.push("/proposals/step-guide");
      });
    } catch (e) {
      console.error("Error parsing proposalFormData for edit:", e);
      alert("Could not parse the saved proposal data for editing.");
    }
  };

  shouldLoadLogosForCurrentRoute = () => {
    const pathname = this.props.location?.pathname || "";
    return (
      pathname.startsWith("/proposals/step-guide") ||
      pathname.startsWith("/proposals/fusion")
    );
  };

  ensureLogosLoaded = async () => {
    if (
      !this._isMounted ||
      !this.state.loggedIn ||
      this.state.logosLoading ||
      (Array.isArray(this.state.logos) && this.state.logos.length > 0)
    ) {
      return;
    }

    this.setState({ logosLoading: true });

    try {
      const proposalLogos = await getLogos().catch((e) => {
        console.log(`${e.message}: getLogos() : Please try again later`);
        throw e;
      });

      if (this._isMounted) {
        let logos;
        try {
          logos = proposalLogos?.body
            ? JSON.parse(proposalLogos.body)
            : proposalLogos;
        } catch (e) {
          console.error("Failed to parse logos:", e);
          logos = [];
        }

        this.setState({
          logos: Array.isArray(logos) ? logos : [],
          logosLoading: false,
        });
      }
    } catch (e) {
      console.error("Failed to load logos:", e);
      if (this._isMounted) {
        this.setState({ logosLoading: false });
      }
    }
  };

  parseApiResponseBody = (response, fallback = []) => {
    if (!response) return fallback;
    if (Array.isArray(response)) return response;

    const { body } = response;
    if (body === undefined || body === null || body === "") return fallback;
    if (typeof body === "string") return JSON.parse(body);
    return body;
  };

  parseLastEvaluatedKey = (response) => {
    if (!response || !response.LastEvaluatedKey) return "";

    const { LastEvaluatedKey } = response;
    if (typeof LastEvaluatedKey !== "string") return LastEvaluatedKey;
    if (LastEvaluatedKey === "" || LastEvaluatedKey === '""') return "";

    return JSON.parse(LastEvaluatedKey);
  };

  loadSubmissionsData = async () => {
    const firstPage = await getSubmissions().catch((e) => {
      console.log(`${e.message}: getSubmissions() : Please try again later`);
      throw e;
    });

    let proposals = this.parseApiResponseBody(firstPage, []);
    let submissionsLastKey = this.parseLastEvaluatedKey(firstPage);
    let additionalSubmissions = [];

    while (submissionsLastKey) {
      const nextPage = await getAdditionalSubmissions(
        1,
        JSON.stringify(submissionsLastKey),
      ).catch((e) => {
        console.log(
          `${e.message}: getAdditionalSubmissions() : Please try again later`,
        );
        throw e;
      });

      const nextSubmissions = this.parseApiResponseBody(nextPage, []);
      proposals = [...proposals, ...nextSubmissions];
      additionalSubmissions = [...additionalSubmissions, ...nextSubmissions];
      submissionsLastKey = this.parseLastEvaluatedKey(nextPage);
    }

    return { proposals, additionalSubmissions, submissionsLastKey };
  };

  getUserStatus = async () => {
    let currentUser = await getCurrentUser().catch((e) => {
      // User is not authenticated - this is expected
      return null;
    });

    if (currentUser) {
      // Get the session to access tokens
      let session = await fetchAuthSession().catch((e) => {
        console.log("Failed to fetch auth session:", e);
        return null;
      });

      if (session && session.tokens && session.tokens.accessToken) {
        this.handleLoggedIn();

        // Start version polling (every 5 minutes + on tab focus) — guard prevents duplicates
        if (!this.versionCheckInterval) {
          this._checkVersion = async () => {
            if (!this._isMounted) return;
            try {
              const res = await fetch(`/version.json?t=${Date.now()}`);
              if (!res.ok) return;
              const { version } = await res.json();
              if (version && version !== window.APP_VERSION) {
                console.info(
                  `New version detected (${window.APP_VERSION} → ${version}). Reloading.`,
                );
                window.location.reload();
              }
            } catch (err) {
              console.warn("Version check failed:", err);
            }
          };
          this.versionCheckInterval = setInterval(
            this._checkVersion,
            5 * 60 * 1000,
          );
          this._onVisibilityChange = () => {
            if (document.visibilityState === "visible") {
              this._checkVersion();
            }
          };
          document.addEventListener(
            "visibilitychange",
            this._onVisibilityChange,
          );
        }

        // Store session in state for inactivity checks
        this.setState({ authSession: session });
        void this.loadSupportAccess();
      } else {
        // User exists in cookies but session/tokens are invalid (expired after inactivity)
        console.log(
          "User found but session is invalid/expired, redirecting to logout",
        );
        this.handleLogOut();
        window.location = "/logout";
        return;
      }

      // Start session & inactivity monitoring (checks every 60 seconds)
      if (!this.sessionCheckInterval) {
        this.lastActivityTime = Date.now();
        this._runSessionCheck(session);
        this.sessionCheckInterval = setInterval(
          this._runSessionCheck,
          60 * 1000,
        );
      }

      this.setState({ user: currentUser.username });
      this.setGroups(
        session.tokens.accessToken.payload["cognito:groups"] || [],
      );

      if (
        this._isMounted &&
        this.state.userStatusRequestSent === false &&
        this.state.loggedIn &&
        !this.state.dataLoaded
      ) {
        this.setState({
          userStatusRequestSent: true,
          proposalTypesLoading: true,
          userDataLoading: true,
          submissionsLoading: true,
          stepGuideDataLoading: true,
        });

        // PHASE 1: Load proposalTypes immediately (cards display)
        try {
          const proposalTypesResult = await getProposalTypes().catch((e) => {
            console.error("Failed to load proposal types:", e);
            throw e;
          });

          if (this._isMounted) {
            let parsedProposalTypes;

            // Handle the API response structure - it returns {statusCode: 200, body: "JSON_STRING"}
            try {
              let dataToProcess;
              if (proposalTypesResult && proposalTypesResult.body) {
                // Parse the JSON string from the body
                dataToProcess = JSON.parse(proposalTypesResult.body);
              } else if (Array.isArray(proposalTypesResult)) {
                dataToProcess = proposalTypesResult;
              } else {
                dataToProcess = proposalTypesResult;
              }

              if (Array.isArray(dataToProcess)) {
                parsedProposalTypes = dataToProcess;
              } else {
                console.warn(
                  "proposalTypes is not an array, using empty array",
                );
                parsedProposalTypes = [];
              }
            } catch (parseError) {
              console.error("Failed to parse proposal types:", parseError);
              parsedProposalTypes = [];
            }

            // Filter out disabled proposal types (enabled !== false)
            let enabledProposalTypes = [];
            if (Array.isArray(parsedProposalTypes)) {
              enabledProposalTypes = parsedProposalTypes.filter(
                (type) => type.enabled !== false,
              );
            } else {
              // If parsedProposalTypes is not an array, treat it as empty
              enabledProposalTypes = [];
            }

            this.setState({
              proposalTypes: enabledProposalTypes,
              proposalTypesLoading: false,
              updateTime: new Date().toLocaleString(),
            });
          }
        } catch (e) {
          console.error("Failed to load proposal types:", e);
          if (this._isMounted) {
            this.setState({
              proposalTypesLoading: false,
              proposalTypes: [], // Set empty array as fallback
            });
          }
          // Only redirect to logout in production
          if (process.env.NODE_ENV === "production") {
            window.location = "/logout";
            return;
          }
        }

        // PHASE 2: Load user data (enables card buttons)
        try {
          const proposalsUserInfo = await getCustomInfo().catch((e) => {
            console.log(
              `${e.message}: getCustomInfo() : Please try again later`,
            );
            throw e;
          });

          if (this._isMounted) {
            // Handle the API response structure for getCustomInfo
            let userInfoData;
            try {
              if (proposalsUserInfo && proposalsUserInfo.body) {
                // Parse the JSON string from the body
                userInfoData = JSON.parse(proposalsUserInfo.body);
              } else {
                userInfoData = proposalsUserInfo;
              }
            } catch (parseError) {
              console.error("Failed to parse user info:", parseError);
              userInfoData = {};
            }

            // Process advisors data
            const advisors = [];
            if (userInfoData) {
              Object.values(userInfoData.Advisors || {}).forEach((element) => {
                advisors.push(element);
              });
            }

            this.setState({
              userInfo: userInfoData,
              advisors: advisors,
              userDataLoading: false,
              // Keep stepGuideDataLoading true until submissions data is loaded
              updateTime: new Date().toLocaleString(),
            });
          }
        } catch (e) {
          console.error("Failed to load user data:", e);
          if (this._isMounted) {
            this.setState({
              userDataLoading: false,
              stepGuideDataLoading: false,
            });
          }
          // Only redirect to logout if it's an authentication error
          if (
            e.name === "NotAuthorizedException" ||
            e.name === "UnauthorizedException"
          ) {
            window.location = "/logout";
          }
          return;
        }

        // PHASE 3: Load remaining essential data in parallel
        const remainingDataPromises = [
          getRegistrationTypes().catch((e) => {
            console.log(
              `${e.message}: getRegistrationTypes() : Please try again later`,
            );
            throw e;
          }),
          getRegistrationCategoryTypes().catch((e) => {
            console.log(
              `${e.message}: getRegistrationCategoryTypes() : Please try again later`,
            );
            throw e;
          }),
          getCustodians().catch((e) => {
            console.log(
              `${e.message}: getCustodians() : Please try again later`,
            );
            return [];
          }),
          getVersions().catch((e) => {
            console.log(`${e.message}: getVersions() : Please try again later`);
            return {};
          }),
        ];

        // Load remaining data needed for form functionality
        try {
          const [
            proposalRegTypesResult,
            proposalRegCategoryTypesResult,
            proposalCustodiansResult,
            currentVersions,
          ] = await Promise.all(remainingDataPromises);

          if (this._isMounted) {
            // Parse the response data for each API call
            let registrationTypes,
              registrationCategoryTypes,
              custodians,
              versions;

            try {
              registrationTypes = proposalRegTypesResult?.body
                ? JSON.parse(proposalRegTypesResult.body)
                : proposalRegTypesResult;
            } catch (e) {
              console.error("Failed to parse registration types:", e);
              registrationTypes = [];
            }

            try {
              registrationCategoryTypes = proposalRegCategoryTypesResult?.body
                ? JSON.parse(proposalRegCategoryTypesResult.body)
                : proposalRegCategoryTypesResult;
            } catch (e) {
              console.error("Failed to parse registration category types:", e);
              registrationCategoryTypes = [];
            }

            try {
              custodians = proposalCustodiansResult?.body
                ? JSON.parse(proposalCustodiansResult.body)
                : proposalCustodiansResult;
            } catch (e) {
              console.error("Failed to parse custodians:", e);
              custodians = [];
            }

            try {
              versions = currentVersions?.body
                ? JSON.parse(currentVersions.body)
                : currentVersions;
            } catch (e) {
              console.error("Failed to parse versions:", e);
              versions = {};
            }

            this.setState({
              registrationTypes: registrationTypes,
              registrationCategoryTypes: registrationCategoryTypes,
              custodians: custodians,
              versions: versions,
              updateTime: new Date().toLocaleString(),
            });
          }
        } catch (e) {
          console.error("Failed to load remaining data:", e);
          if (this._isMounted) {
            this.setState({
              // Continue even if some data failed
            });
          }
        }

        // PHASE 4: Load submissions data independently (required for step-guide)
        try {
          const submissionsData = await this.loadSubmissionsData();

          if (this._isMounted) {
            this.setState({
              proposals: submissionsData.proposals,
              additionalSubmissions: submissionsData.additionalSubmissions,
              submissionsLastKey: submissionsData.submissionsLastKey,
              submissionsLoading: false,
              stepGuideDataLoading: false, // Step-guide is now fully functional with all required data
              userStatusRequestSent: false,
              dataLoaded: true, // Mark data as loaded to prevent re-loading
              updateTime: new Date().toLocaleString(),
            });
          }
        } catch (e) {
          console.error("Failed to load submissions data:", e);
          if (this._isMounted) {
            this.setState({
              submissionsLoading: false,
              stepGuideDataLoading: false, // Allow step-guide to function even if submissions failed
              userStatusRequestSent: false,
              dataLoaded: true, // Still mark as loaded even on error
            });
          }
          // Only redirect to logout if it's an authentication error
          if (
            e.name === "NotAuthorizedException" ||
            e.name === "UnauthorizedException"
          ) {
            window.location = "/logout";
          }
        }
      }
    }
  };

  confirmAlert = async () => {
    // Stop countdown and reset inactivity tracking
    this._stopWarningCountdown();
    this.lastActivityTime = Date.now();

    // Slide the CloudFront edge cookie too, not just the Amplify token.
    if (this._edgeSessionEnabled) {
      sendHeartbeat();
    }

    try {
      const currentSession = await fetchAuthSession({ forceRefresh: true });
      if (!currentSession || !currentSession.tokens) {
        this._logout();
        return;
      }

      this.setState({
        sessionExpireNotificaiton: false,
        alertMessage: "",
        alertButtonMessage: "",
        confirmFunction: null,
        leftAlertDrawerOpen: false,
        warningRemainingSeconds: 0,
      });
    } catch (e) {
      console.log(`${e.message}: confirmAlert : Refresh failed, logging out`);
      this._logout();
    }
  };

  _runSessionCheck = (session = this.state.authSession) => {
    if (!this._isMounted) return;

    const now = Date.now();
    const inactivityDuration = now - this.lastActivityTime;
    const sessionTokenExp = session?.tokens?.accessToken?.payload?.exp;
    const tokenExp = Number.isFinite(sessionTokenExp)
      ? sessionTokenExp
      : auth.getTokenExpiration();
    const tokenExpMs = tokenExp ? tokenExp * 1000 : null;

    if (this._inactivityWarningVisible) return;

    if (inactivityDuration >= INACTIVITY_WARNING_MS) {
      this._startWarningCountdown(inactivityDuration);
      return;
    }

    if (
      !this._isRefreshing &&
      tokenExpMs &&
      tokenExpMs - now < TOKEN_REFRESH_THRESHOLD_MS &&
      tokenExpMs - now > 0
    ) {
      this._isRefreshing = true;
      fetchAuthSession({ forceRefresh: true })
        .then((refreshedSession) => {
          this._isRefreshing = false;
          if (this._isMounted) {
            this.setState({ authSession: refreshedSession });
          }
        })
        .catch(() => {
          this._isRefreshing = false;
          this._logout();
        });
      return;
    }

    if (tokenExpMs && now >= tokenExpMs) {
      this._logout();
      return;
    }

    if (!tokenExpMs && this.state.loggedIn) {
      this._logout();
    }
  };

  _startWarningCountdown = (inactivityDuration) => {
    const now = Date.now();
    this._inactivityWarningVisible = true;
    this._warningStartTime = now - (inactivityDuration - INACTIVITY_WARNING_MS);
    const remaining = INACTIVITY_TIMEOUT_MS - inactivityDuration;
    const remainingSeconds = Math.max(0, Math.ceil(remaining / 1000));

    this.setState({
      sessionExpireNotificaiton: true,
      alertMessage: "You will be logged out due to inactivity",
      alertButtonMessage: "Stay signed in",
      confirmFunction: this.confirmAlert,
      leftAlertDrawerOpen: true,
      warningRemainingSeconds: remainingSeconds,
    });

    if (this._countdownInterval) {
      clearInterval(this._countdownInterval);
    }

    this._countdownInterval = setInterval(() => {
      const elapsed = Date.now() - this._warningStartTime;
      const timeLeft = WARNING_DURATION_MS - elapsed;

      if (timeLeft <= 0) {
        this._stopWarningCountdown();
        this._logout();
        return;
      }

      this.setState({
        warningRemainingSeconds: Math.ceil(timeLeft / 1000),
      });
    }, 1000);
  };

  _stopWarningCountdown = () => {
    if (this._countdownInterval) {
      clearInterval(this._countdownInterval);
      this._countdownInterval = null;
    }

    this._inactivityWarningVisible = false;
    this._warningStartTime = null;
  };

  _logout = () => {
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
      this.sessionCheckInterval = null;
    }
    this._stopWarningCountdown();
    this.clearSessionLockTimeouts();
    this.stopWarningCountdown();
    clearSessionLockCookies();
    window.location = "/logout";
  };

  handleSessionLockCodeChange = (sessionLockCode) => {
    this.setState({ sessionLockCode, sessionLockError: "" });
  };

  handleSessionUnlockConfirm = async () => {
    if (this.state.sessionLockSubmitting) {
      return;
    }

    this.setState({
      sessionLockSubmitting: true,
      sessionLockError: "",
    });

    try {
      let refreshedSession;
      try {
        refreshedSession = await fetchAuthSession({ forceRefresh: true });
      } catch (error) {
        console.error("Session refresh failed during unlock:", error);
        this._logout();
        return;
      }

      if (!refreshedSession?.tokens?.accessToken) {
        this._logout();
        return;
      }

      await verifyUnlock(this.state.sessionLockCode.trim());
      this.setState(
        {
          sessionLocked: false,
          sessionLockWarningVisible: false,
          sessionLockRemainingSeconds: 0,
          sessionLockCode: "",
          sessionLockError: "",
          sessionLockSubmitting: false,
        },
        this.scheduleSessionLockTimers,
      );
    } catch (error) {
      console.error("Failed to unlock session:", error);
      if (error?.message === "Session expired. Please log in again.") {
        this._logout();
        return;
      }
      this.setState({
        sessionLockSubmitting: false,
        sessionLockError:
          error?.message ||
          "Unable to confirm the code. Please try again or log out.",
      });
    }
  };

  clearSessionLockTimeouts = () => {
    if (this._sessionLockWarningTimer) {
      clearTimeout(this._sessionLockWarningTimer);
      this._sessionLockWarningTimer = null;
    }

    if (this._sessionLockTimer) {
      clearTimeout(this._sessionLockTimer);
      this._sessionLockTimer = null;
    }
  };

  stopWarningCountdown = (resetState = true) => {
    if (this._sessionLockCountdownInterval) {
      clearInterval(this._sessionLockCountdownInterval);
      this._sessionLockCountdownInterval = null;
    }

    if (
      resetState &&
      (this.state.sessionLockWarningVisible ||
        this.state.sessionLockRemainingSeconds)
    ) {
      this.setState({
        sessionLockWarningVisible: false,
        sessionLockRemainingSeconds: 0,
      });
    }
  };

  startWarningCountdown = (initialWarningState = getWarningState()) => {
    if (
      !this.state.sessionLockEnabled ||
      !this.state.loggedIn ||
      this.state.sessionLocked ||
      document.visibilityState !== "visible"
    ) {
      return;
    }

    if (initialWarningState.shouldLock) {
      this.triggerSessionLock();
      return;
    }

    if (!initialWarningState.shouldWarn) {
      this.stopWarningCountdown();
      return;
    }

    if (
      !this.state.sessionLockWarningVisible ||
      this.state.sessionLockRemainingSeconds !==
        initialWarningState.remainingSeconds
    ) {
      this.setState({
        sessionLockWarningVisible: true,
        sessionLockRemainingSeconds: initialWarningState.remainingSeconds,
      });
    }

    if (this._sessionLockCountdownInterval) {
      return;
    }

    this._sessionLockCountdownInterval = setInterval(() => {
      if (
        !this.state.sessionLockEnabled ||
        !this.state.loggedIn ||
        this.state.sessionLocked ||
        document.visibilityState !== "visible"
      ) {
        this.stopWarningCountdown();
        return;
      }

      const warningState = getWarningState();
      if (warningState.shouldLock || warningState.remainingSeconds <= 0) {
        this.stopWarningCountdown();
        this.triggerSessionLock();
        return;
      }

      if (!warningState.shouldWarn) {
        this.stopWarningCountdown();
        this.scheduleSessionLockTimers();
        return;
      }

      if (
        !this.state.sessionLockWarningVisible ||
        this.state.sessionLockRemainingSeconds !== warningState.remainingSeconds
      ) {
        this.setState({
          sessionLockWarningVisible: true,
          sessionLockRemainingSeconds: warningState.remainingSeconds,
        });
      }
    }, 1000);
  };

  scheduleSessionLockTimers = () => {
    this.clearSessionLockTimeouts();

    if (!this.state.sessionLockEnabled || !this.state.loggedIn) {
      this.stopWarningCountdown();
      return;
    }

    if (document.visibilityState !== "visible") {
      this.stopWarningCountdown();
      return;
    }

    if (isLocallyLocked()) {
      this.stopWarningCountdown();
      if (!this.state.sessionLocked) {
        this.setState({
          sessionLocked: true,
          sessionLockWarningVisible: false,
          sessionLockRemainingSeconds: 0,
          sessionLockError: "",
        });
      }
      return;
    }

    const warningState = getWarningState();
    if (warningState.shouldLock) {
      this.stopWarningCountdown();
      this.triggerSessionLock();
      return;
    }

    this._sessionLockTimer = setTimeout(() => {
      this.triggerSessionLock();
    }, warningState.msUntilLock);

    if (warningState.shouldWarn) {
      this.startWarningCountdown(warningState);
      return;
    }

    this.stopWarningCountdown();
    this._sessionLockWarningTimer = setTimeout(() => {
      this.startWarningCountdown();
    }, warningState.msUntilWarning);
  };

  initializeSessionLock = async () => {
    if (
      !this.state.sessionLockEnabled ||
      !this.state.loggedIn ||
      this._sessionLockInitialized
    ) {
      return;
    }

    this._sessionLockInitialized = true;
    const context = await enterParticipatingContext().catch((error) => {
      console.warn("Failed to initialize session lock context:", error);
      return null;
    });

    if (context?.startedFresh || context?.sessionChanged) {
      this.setState({
        sessionLocked: false,
        sessionLockWarningVisible: false,
        sessionLockRemainingSeconds: 0,
        sessionLockCode: "",
        sessionLockError: "",
        sessionLockSubmitting: false,
      });
    }

    this._sessionLockBroadcastCleanup = subscribeToLockEvents((event) => {
      if (!event?.type) {
        return;
      }

      if (event.type === "lock") {
        this.clearSessionLockTimeouts();
        this.stopWarningCountdown();
        this.setState({
          sessionLocked: true,
          sessionLockWarningVisible: false,
          sessionLockRemainingSeconds: 0,
          sessionLockError: "",
        });
      }

      if (event.type === "unlock" || event.type === "reset") {
        this.setState(
          {
            sessionLocked: false,
            sessionLockCode: "",
            sessionLockError: "",
            sessionLockSubmitting: false,
            sessionLockWarningVisible: false,
            sessionLockRemainingSeconds: 0,
          },
          this.scheduleSessionLockTimers,
        );
      }

      if (event.type === "activity") {
        const nextState = this.state.sessionLockWarningVisible
          ? {
              sessionLockWarningVisible: false,
              sessionLockRemainingSeconds: 0,
              sessionLockError: "",
            }
          : { sessionLockError: "" };
        this.setState(nextState, this.scheduleSessionLockTimers);
      }
    });

    this._handleSessionActivity = () => {
      if (document.visibilityState !== "visible" || this.state.sessionLocked) {
        return;
      }

      const now = Date.now();
      if (
        this._lastSessionActivityAt &&
        now - this._lastSessionActivityAt < getActivityThrottleMs()
      ) {
        return;
      }

      this._lastSessionActivityAt = now;
      markActivity();
      if (this.state.sessionLockWarningVisible || this.state.sessionLockError) {
        this.setState(
          {
            sessionLockWarningVisible: false,
            sessionLockRemainingSeconds: 0,
            sessionLockError: "",
          },
          this.scheduleSessionLockTimers,
        );
        return;
      }

      this.scheduleSessionLockTimers();
    };

    this._handleSessionVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        this.syncSessionLockState();
        return;
      }

      markPresence();
      this.clearSessionLockTimeouts();
      this.stopWarningCountdown();
    };

    this._handleSessionPageShow = () => {
      this.syncSessionLockState();
    };

    this._handleSessionPageHide = () => {
      markPresence();
      this.clearSessionLockTimeouts();
      this.stopWarningCountdown();
    };

    const activityEvents = [
      "pointerdown",
      "pointermove",
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "click",
    ];
    activityEvents.forEach((event) => {
      window.addEventListener(event, this._handleSessionActivity, {
        passive: true,
      });
    });
    document.addEventListener(
      "visibilitychange",
      this._handleSessionVisibilityChange,
    );
    window.addEventListener("pageshow", this._handleSessionPageShow);
    window.addEventListener("pagehide", this._handleSessionPageHide);

    await this.syncSessionLockState();
  };

  teardownSessionLock = () => {
    this._sessionLockInitialized = false;
    this.clearSessionLockTimeouts();
    this.stopWarningCountdown();

    if (this._sessionLockBroadcastCleanup) {
      this._sessionLockBroadcastCleanup();
      this._sessionLockBroadcastCleanup = null;
    }

    if (this._handleSessionActivity) {
      const activityEvents = [
        "pointerdown",
        "pointermove",
        "mousemove",
        "mousedown",
        "keydown",
        "scroll",
        "touchstart",
        "click",
      ];
      activityEvents.forEach((event) => {
        window.removeEventListener(event, this._handleSessionActivity);
      });
      this._handleSessionActivity = null;
    }

    if (this._handleSessionVisibilityChange) {
      document.removeEventListener(
        "visibilitychange",
        this._handleSessionVisibilityChange,
      );
      this._handleSessionVisibilityChange = null;
    }

    if (this._handleSessionPageShow) {
      window.removeEventListener("pageshow", this._handleSessionPageShow);
      this._handleSessionPageShow = null;
    }

    if (this._handleSessionPageHide) {
      window.removeEventListener("pagehide", this._handleSessionPageHide);
      this._handleSessionPageHide = null;
    }
  };

  syncSessionLockState = async () => {
    if (!this.state.sessionLockEnabled || !this.state.loggedIn) {
      return;
    }

    let context;
    try {
      context = await reconcileParticipatingContext();
    } catch (error) {
      console.warn("Failed to reconcile session lock context:", error);
      this._logout();
      return;
    }

    if (!this._isMounted || !this.state.loggedIn) {
      return;
    }

    if (context.locked) {
      this.clearSessionLockTimeouts();
      this.stopWarningCountdown();
      if (!this.state.sessionLocked) {
        this.setState({
          sessionLocked: true,
          sessionLockWarningVisible: false,
          sessionLockRemainingSeconds: 0,
          sessionLockError: "",
        });
      }
      return;
    }

    if (
      context.startedFresh ||
      context.sessionChanged ||
      this.state.sessionLocked
    ) {
      this.setState(
        {
          sessionLocked: false,
          sessionLockWarningVisible: false,
          sessionLockRemainingSeconds: 0,
          sessionLockCode: "",
          sessionLockError: "",
          sessionLockSubmitting: false,
        },
        this.scheduleSessionLockTimers,
      );
      return;
    }

    this.scheduleSessionLockTimers();
  };

  triggerSessionLock = async () => {
    if (
      this._lockingSession ||
      !this.state.sessionLockEnabled ||
      !this.state.loggedIn ||
      this.state.sessionLocked ||
      document.visibilityState !== "visible"
    ) {
      return;
    }

    this._lockingSession = true;
    this.clearSessionLockTimeouts();
    this.stopWarningCountdown();

    try {
      await lockSession();
    } finally {
      this._lockingSession = false;
      this.setState({
        sessionLocked: true,
        sessionLockWarningVisible: false,
        sessionLockRemainingSeconds: 0,
      });
    }
  };

  // ── Edge session lock methods (active when REACT_APP_VC_SESSION_EDGE_ENABLED=true) ──

  initializeEdgeSessionLock = async () => {
    if (
      !this._edgeSessionEnabled ||
      !this.state.loggedIn ||
      this._sessionLockInitialized
    ) {
      return;
    }

    this._sessionLockInitialized = true;

    // Initial heartbeat to ensure cookie exists
    await sendHeartbeat();

    // BroadcastChannel for heartbeat coordination (separate from legacy channel)
    if (typeof BroadcastChannel !== "undefined") {
      this._edgeHeartbeatChannel = new BroadcastChannel(
        "vc-session-edge-heartbeat",
      );
      this._edgeHeartbeatChannel.addEventListener("message", (event) => {
        if (event.data?.type === "heartbeat-sent") {
          this._lastEdgeHeartbeatAt = event.data.at || Date.now();
        }
      });
    }

    // Activity listener — throttled heartbeat on real user gestures
    this._lastEdgeHeartbeatAt = Date.now();

    this._handleEdgeActivity = () => {
      if (document.visibilityState !== "visible") {
        return;
      }

      const now = Date.now();
      const throttle = 60000; // 1 heartbeat per 60s
      if (
        this._lastEdgeHeartbeatAt &&
        now - this._lastEdgeHeartbeatAt < throttle
      ) {
        return;
      }

      this._lastEdgeHeartbeatAt = now;
      sendHeartbeat();

      if (this._edgeHeartbeatChannel) {
        this._edgeHeartbeatChannel.postMessage({
          type: "heartbeat-sent",
          at: now,
        });
      }
    };

    const activityEvents = [
      "pointerdown",
      "pointermove",
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "click",
    ];
    activityEvents.forEach((event) => {
      window.addEventListener(event, this._handleEdgeActivity, {
        passive: true,
      });
    });
  };

  teardownEdgeSessionLock = () => {
    this._sessionLockInitialized = false;

    if (this._edgeHeartbeatChannel) {
      this._edgeHeartbeatChannel.close();
      this._edgeHeartbeatChannel = null;
    }

    if (this._handleEdgeActivity) {
      const activityEvents = [
        "pointerdown",
        "pointermove",
        "mousemove",
        "mousedown",
        "keydown",
        "scroll",
        "touchstart",
        "click",
      ];
      activityEvents.forEach((event) => {
        window.removeEventListener(event, this._handleEdgeActivity);
      });
      this._handleEdgeActivity = null;
    }
  };

  // ── End edge session lock methods ──

  setGroups = (inputGroups) => {
    this.setState({ groups: inputGroups });
  };

  componentDidMount = () => {
    this.getUserStatus();
    this.setupAuthErrorListener();

    // Inactivity tracking — throttled DOM event listeners
    this._handleUserActivity = () => {
      if (this._inactivityWarningVisible) return;
      const now = Date.now();
      if (now - this._lastActivityUpdate < 30000) return;
      this._lastActivityUpdate = now;
      this.lastActivityTime = now;
    };

    const activityEvents = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "click",
    ];
    activityEvents.forEach((event) => {
      window.addEventListener(event, this._handleUserActivity, {
        passive: true,
      });
    });

    // Tab visibility change — catch up on inactivity when returning
    this._handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (this._inactivityWarningVisible) return;

      const now = Date.now();
      const inactivityDuration = now - this.lastActivityTime;

      if (inactivityDuration >= INACTIVITY_TIMEOUT_MS) {
        this._logout();
      } else if (inactivityDuration >= INACTIVITY_WARNING_MS) {
        this._startWarningCountdown(inactivityDuration);
      }
    };
    document.addEventListener("visibilitychange", this._handleVisibilityChange);
  };

  componentDidUpdate = (prevProps, prevState) => {
    const routeNeedsLogos = this.shouldLoadLogosForCurrentRoute();
    const routeChanged =
      prevProps.location?.pathname !== this.props.location?.pathname;
    const justLoggedIn = !prevState.loggedIn && this.state.loggedIn;
    const justLoggedOut = prevState.loggedIn && !this.state.loggedIn;

    if (routeNeedsLogos && (routeChanged || justLoggedIn)) {
      this.ensureLogosLoaded();
    }

    if (justLoggedIn) {
      if (this._edgeSessionEnabled) {
        this.initializeEdgeSessionLock();
      } else {
        this.initializeSessionLock();
      }
    }

    if (justLoggedOut) {
      if (this._edgeSessionEnabled) {
        this.teardownEdgeSessionLock();
      } else {
        this.teardownSessionLock();
      }
    }
  };

  setupAuthErrorListener = () => {
    // Listen for auth errors from Amplify Hub
    Hub.listen("auth", ({ payload }) => {
      const { event } = payload;

      // Handle token refresh failures
      if (event === "tokenRefresh_failure") {
        console.log("Token refresh failed, redirecting to logout");
        window.location = "/logout";
      }

      // Handle sign out events
      if (event === "signedOut") {
        console.log("User signed out, redirecting to logout");
        window.location = "/logout";
      }
    });
  };

  componentWillUnmount = () => {
    this._isMounted = false;
    if (this._edgeSessionEnabled) {
      this.teardownEdgeSessionLock();
    } else {
      this.teardownSessionLock();
    }

    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
      this.sessionCheckInterval = null;
    }

    if (this._countdownInterval) {
      clearInterval(this._countdownInterval);
      this._countdownInterval = null;
    }

    // Clear version polling
    if (this.versionCheckInterval) {
      clearInterval(this.versionCheckInterval);
    }
    if (this._onVisibilityChange) {
      document.removeEventListener(
        "visibilitychange",
        this._onVisibilityChange,
      );
    }

    if (this._handleVisibilityChange) {
      document.removeEventListener(
        "visibilitychange",
        this._handleVisibilityChange,
      );
    }

    if (this._handleUserActivity) {
      const activityEvents = [
        "mousemove",
        "mousedown",
        "keydown",
        "scroll",
        "touchstart",
        "click",
      ];

      activityEvents.forEach((event) => {
        window.removeEventListener(event, this._handleUserActivity);
      });
    }
  };

  drawerToggleClickHandler = () => {
    this.setState((prevState) => {
      return { sideDrawerOpen: !prevState.sideDrawerOpen };
    });
  };

  backdropClickHandler = () => {
    this.setState({ sideDrawerOpen: false });
  };

  sleep(milliseconds) {
    const date = Date.now();
    let currentDate = null;
    do {
      currentDate = Date.now();
    } while (currentDate - date < milliseconds);
  }

  updateProposalsState = async () => {
    if (
      this._isMounted &&
      this.state.submissionsRequestSent === false &&
      this.state.loggedIn
    ) {
      let currentUser = await getCurrentUser().catch((e) => {
        // User is not authenticated - this is expected
        return null;
      });
      if (currentUser) {
        this.setState({
          submissionsRequestSent: true,
          submissionsLoading: true,
        });

        try {
          // Only refresh submissions and proposal types for the refresh functionality
          const [proposalTypesResult, submissionsData] = await Promise.all([
            getProposalTypes().catch((e) => {
              console.error("Failed to load proposal types:", e);
              throw e;
            }),
            this.loadSubmissionsData(),
          ]);

          if (this._isMounted) {
            // Handle API response format
            let parsedProposalTypes;
            try {
              if (proposalTypesResult?.body) {
                parsedProposalTypes = [...JSON.parse(proposalTypesResult.body)];
              } else if (Array.isArray(proposalTypesResult)) {
                parsedProposalTypes = [...proposalTypesResult];
              } else {
                parsedProposalTypes = [];
              }
            } catch (e) {
              console.error("Error parsing proposal types:", e);
              parsedProposalTypes = [];
            }

            // Filter out disabled proposal types (enabled !== false)
            const newProposalTypes = parsedProposalTypes.filter(
              (type) => type.enabled !== false,
            );

            this.setState({
              proposalTypes: newProposalTypes,
              proposals: submissionsData.proposals,
              additionalSubmissions: submissionsData.additionalSubmissions,
              submissionsLastKey: submissionsData.submissionsLastKey,
              updateTime: new Date().toLocaleString(),
              submissionsRequestSent: false,
              submissionsLoading: false,
            });

            // No need to invalidate caches - removed caching logic
          }
        } catch (e) {
          console.error("Failed to update proposals state:", e);
          if (this._isMounted) {
            this.setState({
              submissionsRequestSent: false,
              submissionsLoading: false,
            });
          }
          // Don't redirect to logout for refresh failures
        }
      }
    }
  };

  updateFormProposalType = (type) => {
    this.setState({
      formProposalType: `${type}`,
      formRequestType: "NewSP",
      insertType: `${type}`.toLowerCase().includes("elements")
        ? "Snapshot"
        : "Fact Sheet",
    });
  };

  updateSubmission = (proposal) => {
    this.setState({ editProposal: proposal, formRequestType: "Edit" });
  };

  copySubmission = (proposal) => {
    // Extract account numbers from the proposal data
    let fusionAccountNumbers = [];

    // Check for direct "ACCOUNT NUMBERS" property (from submissions)
    if (proposal["ACCOUNT NUMBERS"]) {
      fusionAccountNumbers = proposal["ACCOUNT NUMBERS"]
        .split(",")
        .map((num) => num.trim())
        .filter((num) => num);
    }
    // Check for fusionAccountNumbers array
    else if (
      proposal.fusionAccountNumbers &&
      Array.isArray(proposal.fusionAccountNumbers)
    ) {
      fusionAccountNumbers = proposal.fusionAccountNumbers;
    }

    // Add account numbers to the proposal copy
    const proposalWithAccountNumbers = {
      ...proposal,
      fusionAccountNumbers: fusionAccountNumbers,
      isNewFusionClient: fusionAccountNumbers.length === 0,
    };

    this.setState({
      editProposal: proposalWithAccountNumbers,
      formRequestType: "Copy",
    });
  };

  newBlankProposal = () => {
    this.setState({
      formRequestType: this.state.proposalTypes[0],
      editProposal: null,
      ...this.getISPReferenceState(),
    });
  };

  // Handle fusion workflow data passing from step-guide to fusion form
  navigateToFusion = (stepGuideData) => {
    this.setState({
      fusionStepGuideData: stepGuideData,
      formProposalType: stepGuideData.program || "Fusion",
      fusionListMode: true, // Enable fusion list mode when entering fusion workflow
    });
    // Navigate to fusion using React Router
    this.props.history.push("/proposals/fusion");
  };

  // Enable fusion list mode (used when entering fusion workflow)
  enableFusionListMode = () => {
    this.setState({ fusionListMode: true });
  };

  // Disable fusion list mode (used when exiting fusion workflow)
  disableFusionListMode = () => {
    this.setState({ fusionListMode: false });
  };

  clearEditState = async () => {
    if (this._isMounted) {
      this.setState({ editProposal: null });
    }
  };

  updateDetails = () => {
    this.setState((prevState) => {
      return { ...prevState, showDetails: !prevState.showDetails };
    });
  };

  updateLastUpdateDate = (time) => {
    this.setState({ lastUpdateDate: time });
  };

  render() {
    let backdrop;
    const {
      user,
      proposals,
      additionalSubmissions,
      logos,
      proposalTypes,
      userInfo,
      advisors,
      registrationTypes,
      registrationCategoryTypes,
      custodians,
      showDetails,
      versions,
      insertType,
      checkForUpdates,
      lastUpdateDate,
      proposalTypesLoading,
      userDataLoading,
      submissionsLoading,
      stepGuideDataLoading,
    } = this.state;

    // Strategies shown in proposal-generation UIs exclude ISP-only strategies.
    const proposalTypesForProposals = proposalTypes.filter(
      (pt) => !isIspOnlyStrategy(pt),
    );

    // Create simple lists object without caching complexity
    const lists = {
      proposals,
      additionalSubmissions,
      logos,
      proposalTypes,
      proposalTypesForProposals,
      userInfo,
      advisors,
      registrationTypes,
      registrationCategoryTypes,
      custodians,
      versions,
    };

    if (this.state.sideDrawerOpen) {
      backdrop = <BackDrop click={this.backdropClickHandler} />;
    }

    const showSessionLockWarning =
      this.state.sessionLockEnabled &&
      this.state.sessionLockWarningVisible &&
      !this.state.sessionLocked;
    const showInactivityWarning = this.state.leftAlertDrawerOpen;

    return (
      <UpdatePropState.Provider value={this.updateProposalsState}>
        <UpdateFormProposalType.Provider vale={this.updateFormProposalType}>
          <div>
            <NavBar
              drawerClickHandler={this.drawerToggleClickHandler}
              user={user}
              proposalTypes={proposalTypesForProposals}
              proposals={proposals}
              newBlankProposal={this.newBlankProposal}
              clearProposalState={this.clearProposalState}
              proposalTypesLoading={proposalTypesLoading}
              submissionsLoading={submissionsLoading}
              canAccessSupport={this.state.supportAccess}
            />
            <LeftAlertDrawer
              show={showInactivityWarning || showSessionLockWarning}
              message={
                showInactivityWarning
                  ? this.state.alertMessage
                  : "Your session will lock soon due to inactivity."
              }
              remainingSeconds={
                showInactivityWarning
                  ? this.state.warningRemainingSeconds
                  : this.state.sessionLockRemainingSeconds
              }
              totalSeconds={120}
              buttonMessage={
                showInactivityWarning
                  ? this.state.alertButtonMessage
                  : undefined
              }
              onConfirm={
                showInactivityWarning ? this.state.confirmFunction : undefined
              }
            />
            <SessionLockModal
              show={this.state.sessionLockEnabled && this.state.sessionLocked}
              code={this.state.sessionLockCode}
              error={this.state.sessionLockError}
              isSubmitting={this.state.sessionLockSubmitting}
              onCodeChange={this.handleSessionLockCodeChange}
              onConfirm={this.handleSessionUnlockConfirm}
              onLogout={this._logout}
            />
            <SideDrawer
              user={user}
              show={this.state.sideDrawerOpen}
              newBlankProposal={this.newBlankProposal}
              clearProposalState={this.clearProposalState}
              canAccessSupport={this.state.supportAccess}
            />
            {backdrop}
            <main className="product-main">
              <Switch>
                <Route
                  path="/login"
                  component={(props) => (
                    <LoginForm {...props} userStatus={this.getUserStatus} />
                  )}
                />
                <Route
                  path="/logout"
                  component={(props) => (
                    <Logout
                      {...props}
                      userStatus={this.getUserStatus}
                      handleLogOut={this.handleLogOut}
                    />
                  )}
                />
                <Route path="/forgot" component={ResetPassword} />
                <ProtectedRoute
                  exact
                  path="/proposals"
                  render={(props) => (
                    <Proposals
                      {...props}
                      proposalTypes={proposalTypesForProposals}
                      updateFormProposalType={this.updateFormProposalType}
                      proposalTypesLoading={proposalTypesLoading}
                      userDataLoading={userDataLoading}
                    />
                  )}
                />
                <ProtectedRoute
                  path="/proposals/step-guide"
                  render={(props) => {
                    // Check if ISP stored pre-fill data for this proposal
                    this.checkForISPProposalData();
                    return (
                      <ProposalStepGuide
                        {...props}
                        updateLastUpdateDate={this.updateLastUpdateDate}
                        checkForUpdates={checkForUpdates}
                        lastUpdateDate={lastUpdateDate}
                        insertType={insertType}
                        lists={lists}
                        editProposal={this.state.editProposal}
                        formRequestType={this.state.formRequestType}
                        formProposalType={this.state.formProposalType}
                        loggedIn={this.state.loggedIn}
                        dataLoading={stepGuideDataLoading}
                        navigateToFusion={this.navigateToFusion}
                        ispReferenceClients={this.state.ispReferenceClients}
                        ispReferenceAmount={this.state.ispReferenceAmount}
                        ispReferenceGoal={this.state.ispReferenceGoal}
                        ispReferenceFusionAllocations={
                          this.state.ispReferenceFusionAllocations
                        }
                        dismissISPReferenceBanner={
                          this.dismissISPReferenceBanner
                        }
                      />
                    );
                  }}
                />
                <ProtectedRoute
                  path="/submissions"
                  render={(props) => (
                    <ProposalSubmissions
                      {...props}
                      showDetails={showDetails}
                      lists={lists}
                      updateDetails={this.updateDetails}
                      newBlankProposal={this.newBlankProposal}
                      clearProposalState={this.clearProposalState}
                      updateSubmission={this.updateSubmission}
                      copySubmission={this.copySubmission}
                      updateTime={this.state.updateTime}
                      proposalTypes={this.state.proposalTypes}
                      proposals={this.state.proposals}
                      updateProposalsState={this.updateProposalsState}
                      submissionsLastKey={this.state.submissionsLastKey}
                      submissionsRequestSent={this.state.submissionsRequestSent}
                      submissionsLoading={submissionsLoading}
                      onCreateProposalFromISP={
                        this.createProposalFromISPSubmission
                      }
                      onCreateISPFromProposal={
                        this.createISPFromProposalSubmission
                      }
                      onEditISP={this.editISPSubmission}
                      onEditProposal={this.editProposalSubmission}
                    />
                  )}
                />
                <ProtectedRoute path="/user" component={User} />
                <ProtectedRoute path="/support" component={SupportPage} />
                <ProtectedRoute
                  path="/proposals/fusion"
                  component={(props) => (
                    <FusionProposal
                      {...props}
                      user={user}
                      proposalTypes={proposalTypesForProposals}
                      editProposal={this.state.editProposal}
                      formRequestType={this.state.formRequestType}
                      lists={lists}
                      fusionStepGuideData={this.state.fusionStepGuideData}
                      fusionListMode={this.state.fusionListMode}
                      enableFusionListMode={this.enableFusionListMode}
                      disableFusionListMode={this.disableFusionListMode}
                      redirectToStepGuideWithFusion={
                        this.redirectToStepGuideWithFusion
                      }
                      ispReferenceClients={this.state.ispReferenceClients}
                      ispReferenceAmount={this.state.ispReferenceAmount}
                      ispReferenceGoal={this.state.ispReferenceGoal}
                      ispReferenceFusionAllocations={
                        this.state.ispReferenceFusionAllocations
                      }
                      dismissISPReferenceBanner={this.dismissISPReferenceBanner}
                    />
                  )}
                />
                <ProtectedRoute
                  path="/plan/step-guide"
                  render={(props) => (
                    <ISPStepGuide
                      {...props}
                      lists={lists}
                      loggedIn={this.state.loggedIn}
                      dataLoading={stepGuideDataLoading}
                      navigateToProposalFromISP={this.navigateToProposalFromISP}
                      updateProposalsState={this.updateProposalsState}
                      user={this.state.user}
                    />
                  )}
                />
                <ProtectedRoute
                  path="/paperwork"
                  component={(props) => (
                    <ProposalDocuments
                      {...props}
                      user={user}
                      registrationTypes={registrationTypes}
                      userInfo={userInfo}
                      proposalTypes={this.state.proposalTypes}
                      clearProposalState={this.clearProposalState}
                      onCreateISPFromProposal={
                        this.createISPFromProposalSubmission
                      }
                    />
                  )}
                />
                <Route path="/not-found" component={NotFound} />
                <Redirect from="/" exact to="/proposals" />
                <Redirect to="/not-found" />
              </Switch>
            </main>
          </div>
        </UpdateFormProposalType.Provider>
      </UpdatePropState.Provider>
    );
  }
}

export default withRouter(App);
